import * as React from 'react';
import { IPublicSession, userSession } from 'state/session';
import { classNames } from 'utils/ui/css';

type ExperienceName = IPublicSession['activeExperience'];

export interface ExperienceConfig {
  name: ExperienceName;
  element: React.ReactElement;
}

export interface ExperienceSwitcherProps {
  experiences: ExperienceConfig[];
  activeExperience: ExperienceName;
}

export const ExperienceSwitcher: React.FC<ExperienceSwitcherProps> = ({ experiences, activeExperience }) => {
  const session = userSession().useState();

  // Memoize experience name map for fast lookups
  const experienceMap = React.useMemo(() => new Map(experiences.map((exp) => [exp.name, exp])), [experiences]);

  // Determine initial active experience from storage or fallback to default
  const getInitialExperience = React.useCallback((): ExperienceName | undefined => {
    // Try to get from session first (for testing), then from activeExperience prop
    const initialExperience = session.activeExperience || activeExperience;
    if (initialExperience && experienceMap.has(initialExperience)) {
      return initialExperience;
    }

    // Fall back to first available experience or undefined if none available
    return experiences.length > 0 ? experiences[0].name : undefined;
  }, [experienceMap, activeExperience, experiences, session.activeExperience]);

  // Use the actual current experience, with proper fallback logic
  const currentExperience = React.useMemo(() => {
    const experience = session.activeExperience || activeExperience;

    if (experience && experienceMap.has(experience)) {
      return experience;
    }

    // Fall back to first experience
    return experiences.length > 0 ? experiences[0].name : undefined;
  }, [session.activeExperience, experienceMap, activeExperience, experiences]);

  // Ref to the current experience container for imperative access
  const experienceContainerRef = React.useRef<HTMLDivElement>(null);

  // Get the current active experience component
  const activeExperienceConfig = React.useMemo(() => {
    return currentExperience ? experienceMap.get(currentExperience) : undefined;
  }, [experienceMap, currentExperience]);

  React.useEffect(() => {
    if (currentExperience && !experienceMap.has(currentExperience)) {
      const initialExperience = getInitialExperience();
      if (initialExperience) {
        userSession().setValues({ activeExperience: initialExperience });
      }
    }
  }, [currentExperience, experienceMap, getInitialExperience]);

  // Sync active experience with the session state
  React.useEffect(() => {
    const sessionExperience = session.activeExperience;

    // If no session experience is set, save the initial one
    if (!sessionExperience) {
      const initialExperience = getInitialExperience();
      if (initialExperience) {
        userSession().setValues({ activeExperience: initialExperience });
      }
    }
    // Run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Early return for empty experiences
  if (experiences.length === 0) {
    return null;
  }

  return (
    <div
      ref={experienceContainerRef}
      role="tabpanel"
      aria-live="polite"
      data-testid="experience-switcher"
      className={classNames(
        !(userSession().hasStreamingId && session.activeExperience === '3D') &&
          'size-full flex-grow e3d-experience-switcher'
      )} // The 3D stream handles sizing itself, but in all other cases we want to fill the container
    >
      {activeExperienceConfig ? (
        <div data-experience={activeExperienceConfig.name} className="ex3d-active-experience size-full">
          {activeExperienceConfig.element}
        </div>
      ) : null}
    </div>
  );
};

ExperienceSwitcher.displayName = 'ExperienceSwitcher';
